<template>
  <ResizablePage>
    <template v-slot:content="conentProps">
      <v-container fluid v-bind:style="{ height: conentProps.tableHeight + 'px' }" class="overflow-y-auto" fill-height>
        <v-layout justify-center>
          <v-row style="max-width: 900px">
            <validation-observer style="width: 100%"
                                 class="px-5"
                                 ref="form_validation"
            >
              <v-form
                @submit.prevent="submit"
                :readonly="!editable">

                <v-expansion-panels multiple flat hover v-model="panels">
                  <v-expansion-panel>
                    <v-expansion-panel-header>{{ $t("view.person.group.base") }}</v-expansion-panel-header>
                    <v-expansion-panel-content>

                      <v-row>
                        <v-col>
                          <validation-provider
                            v-slot="{ errors }"
                            name="view.person.first_name"
                            rules="max:100"
                          >
                            <v-text-field
                              v-model="firstName"
                              :counter="100"
                              :error-messages="errors"
                              :label="$t('view.person.first_name')"
                              required
                            ></v-text-field>
                          </validation-provider>
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col>
                          <validation-provider
                            v-slot="{ errors }"
                            name="view.person.last_name"
                            rules="required|max:100"
                          >
                            <v-text-field
                              v-model="lastName"
                              :counter="100"
                              :error-messages="errors"
                              :label="$t('view.person.last_name')"
                              required
                            ></v-text-field>
                          </validation-provider>
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col>
                          <validation-provider
                            v-slot="{ errors }"
                            name="view.person.login"
                            rules="max:50"
                          >
                            <v-text-field
                              v-model="login"
                              :counter="50"
                              :error-messages="errors"
                              :label="$t('view.person.login')"
                              required
                              autocomplete="off"
                            ></v-text-field>
                          </validation-provider>
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col>
                          <validation-provider
                            v-slot="{ errors }"
                            name="view.person.password"
                            rules="min:8|max:50"
                          >
                            <v-text-field
                              v-model="password"
                              type="password"
                              :counter="50"
                              :error-messages="errors"
                              :label="$t('view.person.password')"
                              required
                              autocomplete="new-password"
                            ></v-text-field>
                          </validation-provider>
                        </v-col>
                      </v-row>
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                  <v-expansion-panel>
                    <v-expansion-panel-header>{{ $t("view.person.group.ids_perms") }}</v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-card elevation="0" outlined>
                            <v-card-title>{{ $t("view.person.role_title") }}</v-card-title>
                            <v-card-text>
                              <v-checkbox
                                v-model="admin"
                                :value="admin"
                                :label="$t('view.person.admin')"
                                type="checkbox"
                              />
                              <v-checkbox
                                v-model="dispatcher"
                                :value="dispatcher"
                                :label="$t('view.person.dispatcher')"
                                type="checkbox"
                              />
                              <v-checkbox
                                v-model="driver"
                                :value="driver"
                                :label="$t('view.person.driver')"
                                type="checkbox"
                              />

                              <v-divider class="pa-2" />

                              <v-checkbox
                                v-model="active"
                                :value="active"
                                :label="$t('view.person.active')"
                                class="mt-0"
                                type="checkbox"
                              />
                              <v-checkbox
                                v-model="viewAll"
                                :value="viewAll"
                                :label="$t('view.person.viewall')"
                                type="checkbox"
                              />
                            </v-card-text>
                          </v-card>
                        </v-col>

                        <v-col cols="12" md="6">
                          <v-card elevation="0" outlined>
                            <v-card-title>{{ $t("view.person.identification_title") }}
                              <v-spacer />
                              <v-btn @click="addIdentificationDialog"
                                     color="primary"
                                     v-if="editable">
                                {{ $t("view.person.add_identification") }}
                              </v-btn>
                            </v-card-title>

                            <v-list v-if="identifications.length > 0">
                              <template v-for="item in identifications">
                                <v-list-item :key="item.code + '/'  + item.identification">
                                  <v-list-item-content>
                                    <v-list-item-title>{{ item.identification }}</v-list-item-title>
                                    <v-list-item-subtitle>{{ item.code }}</v-list-item-subtitle>
                                  </v-list-item-content>
                                  <v-list-item-action>
                                    <v-tooltip bottom open-delay="500">
                                      <template v-slot:activator="{ on }">
                                        <v-btn icon
                                               @click="removeIdentification(item.identification, item.code)"
                                               v-on="on"
                                               v-if="editable">
                                          <v-icon color="grey lighten-1">mdi-delete</v-icon>
                                        </v-btn>
                                      </template>
                                      <span>{{ $t("view.person.tooltip.id_remove") }}</span>
                                    </v-tooltip>
                                  </v-list-item-action>
                                </v-list-item>
                              </template>
                            </v-list>
                            <div v-else class="text-center pa-2 pb-4 grey--text"
                                 v-html="$t('common.empty_list_with_add')"></div>
                          </v-card>
                        </v-col>
                      </v-row>
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                  <v-expansion-panel v-if="!viewAll">
                    <v-expansion-panel-header>{{ $t("view.person.group.vehicles") }}</v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-row>
                        <v-col>
                          <ItemsSelection
                            v-model="visibleVehicles"
                            :items="allVehicles"
                            :mapper="vehicleMapper" />
                        </v-col>
                      </v-row>
                    </v-expansion-panel-content>
                  </v-expansion-panel>


                  <v-expansion-panel v-if="!viewAll">
                    <v-expansion-panel-header>{{ $t("view.person.group.persons") }}</v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-row>
                        <v-col>
                          <ItemsSelection
                            v-model="visiblePersons"
                            :items="allPersons"
                            :mapper="personMapper" />
                        </v-col>
                      </v-row>
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                  <v-expansion-panel v-if="companies != null">
                    <v-expansion-panel-header>{{ $t("view.person.group.companies") }}</v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-row>
                        <v-col cols="12">
                          <v-list>
                            <v-list-item v-for="item in companies" :key="item.companyId">
                              <v-list-item-icon>
                                <country-flag :country="item.countryCode" size="normal" />
                              </v-list-item-icon>
                              <v-list-item-content class="mt-1">
                                <v-list-item-title>{{ item.name }}</v-list-item-title>
                                <v-list-item-subtitle>{{ getRoles(item) }}</v-list-item-subtitle>
                              </v-list-item-content>
                            </v-list-item>
                          </v-list>
                        </v-col>
                      </v-row>
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                </v-expansion-panels>
              </v-form>
            </validation-observer>
          </v-row>
        </v-layout>

        <v-snackbar
          v-model="showError"
          bottom
          light
          timeout="-1"
          color="red lighten-4"
        >
          {{ errorMessage }}

          <template v-slot:action="{ attrs }">
            <v-btn
              color="indigo"
              text
              v-bind="attrs"
              @click="showError = false"
            >
              Close
            </v-btn>
          </template>
        </v-snackbar>

        <IdentificationDialog
          ref="identificationDialog"
          filter="person"
          :ids="identifications"
          v-on:added="addIdentification"
        />
      </v-container>
    </template>
  </ResizablePage>
</template>

<script>
import utils from "@/utils";
import IdentificationDialog from "@/components/dialogs/IdentificationDialog";
import ResizablePage from "@/components/ResizablePage";
import ItemsSelection from "@/components/ItemsSelection.vue";

export default {
  name: "PersonView",
  components: { ItemsSelection, IdentificationDialog, ResizablePage },
  data() {
    return {
      editable: this.canWrite("person"),
      canLeave: !this.canWrite("person"),
      panels: [0],

      firstName: null,
      lastName: "",
      login: null,
      password: null,
      active: true,

      admin: false,
      dispatcher: true,
      driver: false,
      viewAll: true,

      identifications: [],
      visibleVehicles: [],
      visiblePersons: [],
      companies: null,

      showError: false,
      errorMessage: null,

      allPersons: [],
      allVehicles: [],

      record: null
    };
  },

  methods: {
    async store() {
      if (await this.$refs.form_validation.validate()) {

        let object = {
          firstName: this.firstName,
          lastName: this.lastName,
          login: utils.nullString(this.login),
          password: utils.nullString(this.password),
          active: this.active,

          admin: this.admin,
          dispatcher: this.dispatcher,
          driver: this.driver,
          viewAll: this.viewAll,

          identifications: this.identifications,
          visibleVehicles: this.visibleVehicles,
          visiblePersons: this.visiblePersons
        };

        let params = {
          module: "person",
          object: object
        };

        if (!utils.isObjectEmpty(this.$store.state.data.record)) {
          params.id = this.record.personId;
          object.personId = this.record.personId;
          object.personLastChange = this.record.personLastChange;
          object.companyPersonLastChange = this.record.companyPersonLastChange;
        }

        let response = await this.$store.dispatch("data/storeRecord", params);

        if (response.error) {
          this.showError = true;
          this.errorMessage = response.message;
        } else {
          this.canLeave = true;
          this.$router.close({ name: "data/persons" });
        }
      }
    },

    async remove() {
      let params = {
        module: "person",
        id: this.record.personId
      };

      let response = await this.$store.dispatch("data/deleteRecord", params);

      if (response.error) {
        this.showError = true;
        this.errorMessage = response.message;
      } else {
        this.canLeave = true;
        this.$router.close({ name: "data/persons" });
      }
    },

    cancel() {
      this.canLeave = true;
      this.$router.close({ name: "data/persons" });
    },

    removeIdentification(identification, code) {
      this.identifications = this.identifications.filter(x => x.identification !== identification || x.code !== code);
    },

    addIdentificationDialog() {
      this.$refs.identificationDialog.open();
    },

    addIdentification(type, code) {
      let id = {
        identification: type,
        code: code
      };

      this.identifications.push(id);
    },

    getRoles(person) {
      return utils.personRoles(person).map((item) => this.$t("roles." + item)).join(", ");
    },

    personMapper(item) {
      return {
        id: item.personId,
        name: item.firstName + " " + item.lastName,
        note: utils.personRoles(item, true).map((item) => this.$t("roles." + item)).join(", "),
        obj: item
      };
    },

    vehicleMapper(item) {
      return {
        id: item.vehicleId,
        name: item.numericPlate,
        note: item.name + " ⦁ " + this.$t("enum.vehicle_type." + item.typeCode),
        obj: item
      };
    }
  },

  watch: {
    "$store.state.data.record": function() {
      if (this.$store.state.data.module === "person" && this.$store.state.data.record !== null) {
        let record = this.$store.state.data.record;

        if (record.personId === undefined) {
          this.firstName = null;
          this.lastName = null;
          this.login = null;
          this.password = null;
          this.active = true;

          this.admin = false;
          this.dispatcher = false;
          this.driver = false;
          this.viewAll = true;

          this.identifications = [];
          this.companies = null;
        } else {
          this.firstName = record.firstName;
          this.lastName = record.lastName;
          this.login = record.login;
          this.password = record.password;
          this.active = record.active;

          this.admin = record.admin;
          this.dispatcher = record.dispatcher;
          this.driver = record.driver;
          this.viewAll = record.viewAll;

          this.identifications = record.identifications || [];
          this.visibleVehicles = record.visibleVehicles || [];
          this.visiblePersons = record.visiblePersons || [];
          this.companies = record.companies;
        }

        this.record = record;
      }
    },

    "$store.state.data.activeEvent": function() {
      const e = this.$store.state.data.activeEvent;
      if (e != null && e.target === "view") {

        try {
          if (e.click !== undefined) {
            switch (e.click) {
              case "store":
                this.store();
                break;
              case "cancel":
                this.cancel();
                break;
              case "delete":
                this.remove();
                break;
            }
          }
        } finally {
          this.$store.dispatch("data/processEvents");
        }
      }
    }
  },

  mounted() {
    this.$store.dispatch("data/resetEvents");

    Promise.all([this.$store.dispatch("data/fetchVehicles", false), this.$store.dispatch("data/fetchPersons", false)])
      .then(result => {
        this.allVehicles = result[0].data;
        this.allPersons = result[1].data;
      });

    if (this.$route.params.id === "new")
      this.$store.dispatch("data/fetchRecord", { module: "person" });
    else
      this.$store.dispatch("data/fetchRecord", { module: "person", id: this.$route.params.id });
  },

  async beforeRouteLeave(to, from, next) {
    if (this.canLeave)
      next();
    else {
      const answer = window.confirm(this.$t("view.leave_msg"));
      if (answer) {
        next();
      } else {
        next(false);
      }
    }
  }
};
</script>

<style scoped>

</style>